<form [formGroup]="form" (ngSubmit)="onSubmit()">

  <!-- ═══════════════════════════════ INPUTS ═════════════════════════════════ -->
  <p-divider align="left">
    <div class="inline-flex align-items-center font-bold">Inputs</div>
  </p-divider>

  <p class="my-2">
    Select an image file or an <b>expanded</b> folder containing one or more fluorescence
    image files in the file browser.
  </p>
  <br>

  <!-- Model -->
  <div class="field grid">
    <label for="model" class="col-12 mb-2 md:col-3 md:mb-0 text-black-alpha-90">
      Model
      <i class="pi pi-info-circle px-1"
         pTooltip="The Cellpose model to use. Picking Cellpose-SAM (v4) routes the run to
         the v4 transformer worker (jit-cellpose-sam-worker) and hides the v3-only tuning
         knobs below. Any other choice (Cytoplasm 3, Nuclei, etc.) routes to the legacy v3
         U-Net worker. Use Custom Model to supply your own v3 weights."
         tooltipPosition="top"></i>
    </label>
    <div class="col-12 md:col-9">
      <p-dropdown
        id="model"
        formControlName="model"
        placeholder="Select Model"
        [options]="modelOptions"
        optionLabel="label"
        optionValue="value"
      ></p-dropdown>
    </div>
  </div>

  <!-- Custom model path (only when CUSTOM selected) -->
  <div class="field grid" *ngIf="isCustomModel">
    <label for="modelLocationPath" class="col-12 mb-2 md:col-3 md:mb-0 text-black-alpha-90">
      Custom Model Path
      <i class="pi pi-info-circle px-1"
         pTooltip="Path to the custom model weights file within the project bucket 
				 (e.g. models/my_model.pth)."
         tooltipPosition="top"></i>
    </label>
    <div class="col-12 md:col-9">
      <input id="modelLocationPath" type="text" pInputText
             formControlName="modelLocationPath"
             placeholder="models/my_cellpose_model.pth"
             class="p-inputtext-sm w-full" />
    </div>
  </div>

  <!-- Channel Mode -->
  <div class="field grid">
    <label for="channelMode" class="col-12 mb-2 md:col-3 md:mb-0 text-black-alpha-90">
      Channel Mode
      <i class="pi pi-info-circle px-1"
         pTooltip="Grayscale: single-channel image. Two Channel: separate cytoplasm 
				 and nucleus channels. Three Channel: RGB image."
         tooltipPosition="top"></i>
    </label>
    <div class="col-12 md:col-9">
      <p-dropdown
        id="channelMode"
        formControlName="channelMode"
        placeholder="Select Channel Mode"
        [options]="channelModeOptions"
        optionLabel="label"
        optionValue="value"
      ></p-dropdown>
    </div>
  </div>

  <!-- Cyto / Nuc channel indices (only for multi-channel) -->
  <ng-container *ngIf="isMultiChannel">
    <div class="field grid">
      <label for="cytoChannel" class="col-12 mb-2 md:col-7 md:mb-0 text-black-alpha-90">
        Cytoplasm Channel Index
        <i class="pi pi-info-circle px-1"
           pTooltip="1-based index of the channel containing cytoplasm signal (e.g. GFP)."
           tooltipPosition="top"></i>
      </label>
      <div class="col-12 md:col-5">
        <p-inputNumber id="cytoChannel" formControlName="cytoChannel"
                       [showButtons]="true" [min]="1" [max]="10"
                       class="p-inputtext-sm"></p-inputNumber>
      </div>
    </div>

    <div class="field grid">
      <label for="nucChannel" class="col-12 mb-2 md:col-7 md:mb-0 text-black-alpha-90">
        Nucleus Channel Index
        <i class="pi pi-info-circle px-1"
           pTooltip="1-based index of the channel containing nucleus signal (e.g. DAPI). 
					 Use 0 if no nucleus channel."
           tooltipPosition="top"></i>
      </label>
      <div class="col-12 md:col-5">
        <p-inputNumber id="nucChannel" formControlName="nucChannel"
                       [showButtons]="true" [min]="0" [max]="10"
                       class="p-inputtext-sm"></p-inputNumber>
      </div>
    </div>
  </ng-container>

  <!-- ═══════════════════════════ PARAMETERS ════════════════════════════════ -->
  <p-divider align="left">
    <div class="inline-flex align-items-center font-bold">Parameters</div>
  </p-divider>

  <!-- Diameter -->
  <div class="field grid">
    <label for="diameter" class="col-12 mb-2 md:col-7 md:mb-0 text-black-alpha-90">
      Cell Diameter (px)
      <i class="pi pi-info-circle px-1"
         pTooltip="Expected cell diameter in pixels. Set to 0 to let CellPose estimate automatically."
         tooltipPosition="top"></i>
    </label>
    <div class="col-12 md:col-5">
      <p-inputNumber id="diameter" formControlName="diameter"
                     [showButtons]="true" [min]="0" [step]="5"
                     [minFractionDigits]="0" [maxFractionDigits]="1"
                     mode="decimal"
                     class="p-inputtext-sm"></p-inputNumber>
    </div>
  </div>

  <!-- Flow Threshold (v3 only — Cellpose-SAM doesn't use flow thresholding) -->
  <div class="field grid" *ngIf="!isCpsamModel">
    <label for="flowThreshold" class="col-12 mb-2 md:col-7 md:mb-0 text-black-alpha-90">
      Flow Threshold
      <i class="pi pi-info-circle px-1"
         pTooltip="Maximum allowed error of the flows for each mask. Increase (up to 3.0)
				 to find more cells; decrease to improve quality. Default: 0.4."
         tooltipPosition="top"></i>
    </label>
    <div class="col-12 md:col-5">
      <p-inputNumber id="flowThreshold" formControlName="flowThreshold"
                     [showButtons]="true" [min]="-5" [max]="5" [step]="0.1"
                     [minFractionDigits]="1" [maxFractionDigits]="2"
                     mode="decimal"
                     class="p-inputtext-sm"></p-inputNumber>
    </div>
  </div>

  <!-- Cellprob Threshold -->
  <div class="field grid">
    <label for="cellprobThreshold" class="col-12 mb-2 md:col-7 md:mb-0 text-black-alpha-90">
      Cell Probability Threshold
      <i class="pi pi-info-circle px-1"
         pTooltip="Pixels above this threshold are used to run dynamics. Range: -6 to 6. 
				 Lower values find more cells. Default: 0.0."
         tooltipPosition="top"></i>
    </label>
    <div class="col-12 md:col-5">
      <p-inputNumber id="cellprobThreshold" formControlName="cellprobThreshold"
                     [showButtons]="true" [min]="-6" [max]="6" [step]="0.5"
                     [minFractionDigits]="1" [maxFractionDigits]="2"
                     mode="decimal"
                     class="p-inputtext-sm"></p-inputNumber>
    </div>
  </div>

  <!-- v3-only knobs: min-size, do3D, normalize, invert, and the Advanced Settings panel
       below. Cellpose-SAM doesn't expose any of these — the v4 worker handles tiling,
       batching, and normalisation internally. -->
  <ng-container *ngIf="!isCpsamModel">

  <!-- Min Size -->
  <div class="field grid">
    <label for="minSize" class="col-12 mb-2 md:col-7 md:mb-0 text-black-alpha-90">
      Minimum Cell Size (px²)
      <i class="pi pi-info-circle px-1"
         pTooltip="Minimum number of pixels per cell mask. Masks smaller than this are discarded. 
				 Default: 15."
         tooltipPosition="top"></i>
    </label>
    <div class="col-12 md:col-5">
      <p-inputNumber id="minSize" formControlName="minSize"
                     [showButtons]="true" [min]="1"
                     class="p-inputtext-sm"></p-inputNumber>
    </div>
  </div>

  <!-- Do 3D -->
  <div class="field-checkbox">
    <p-checkbox formControlName="do3D" [binary]="true" class="my-1" id="do3D"></p-checkbox>
    <label for="do3D">
      Run in 3D mode
      <i class="pi pi-info-circle px-2"
         pTooltip="Process a Z-stack as a true 3D volume rather than slice-by-slice. 
				 Requires the input to be a multi-Z image."
         tooltipPosition="top"></i>
    </label>
  </div>

  <!-- Normalize -->
  <div class="field-checkbox">
    <p-checkbox formControlName="normalize" [binary]="true" class="my-1" id="normalize"></p-checkbox>
    <label for="normalize">
      Normalize image
      <i class="pi pi-info-circle px-2"
         pTooltip="Normalise each image tile to [1st, 99th] percentile before inference. 
				 Recommended for most fluorescence images."
         tooltipPosition="top"></i>
    </label>
  </div>

  <!-- Invert -->
  <div class="field-checkbox">
    <p-checkbox formControlName="invert" [binary]="true" class="my-1" id="invert"></p-checkbox>
    <label for="invert">
      Invert image grayscale
      <i class="pi pi-info-circle px-2"
         pTooltip="Invert the image before running inference. Useful for bright-field images 
				 where cells are darker than the background."
         tooltipPosition="top"></i>
    </label>
  </div>

  <!-- ═════════════════════════ ADVANCED SETTINGS ════════════════════════════ -->
  <p-panel header="Advanced Settings" [toggleable]="true" [collapsed]="true" styleClass="mt-3 mb-2">

    <!-- Augment -->
    <div class="field-checkbox">
      <p-checkbox formControlName="augment" [binary]="true" class="my-1" id="augment"></p-checkbox>
      <label for="augment">
        Augment (tiles × 4)
        <i class="pi pi-info-circle px-2"
           pTooltip="Tiles the image 4 times with different rotations and averages the results. 
					 Increases quality but is 4× slower."
           tooltipPosition="top"></i>
      </label>
    </div>

    <!-- Resample -->
    <div class="field-checkbox">
      <p-checkbox formControlName="resample" [binary]="true" class="my-1" id="resample"></p-checkbox>
      <label for="resample">
        Resample dynamics
        <i class="pi pi-info-circle px-2"
           pTooltip="Run dynamics at the original image resolution rather than at the downsampled 
					 size. Produces more accurate outlines."
           tooltipPosition="top"></i>
      </label>
    </div>

    <!-- Tile Overlap -->
    <div class="field grid">
      <label for="tileOverlap" class="col-12 mb-2 md:col-7 md:mb-0 text-black-alpha-90">
        Tile Overlap
        <i class="pi pi-info-circle px-1"
           pTooltip="Fractional overlap between adjacent tiles when tiling large images (0–0.9). 
					 Default: 0.1."
           tooltipPosition="top"></i>
      </label>
      <div class="col-12 md:col-5">
        <p-inputNumber id="tileOverlap" formControlName="tileOverlap"
                       [showButtons]="true" [min]="0" [max]="0.9" [step]="0.05"
                       [minFractionDigits]="1" [maxFractionDigits]="2"
                       mode="decimal"
                       class="p-inputtext-sm"></p-inputNumber>
      </div>
    </div>

    <!-- Batch Size -->
    <div class="field grid">
      <label for="batchSize" class="col-12 mb-2 md:col-7 md:mb-0 text-black-alpha-90">
        Batch Size
        <i class="pi pi-info-circle px-1"
           pTooltip="Number of 224×224 patches to run through the network simultaneously. 
					 Reduce if GPU runs out of memory. Default: 8."
           tooltipPosition="top"></i>
      </label>
      <div class="col-12 md:col-5">
        <p-inputNumber id="batchSize" formControlName="batchSize"
                       [showButtons]="true" [min]="1" [max]="64"
                       class="p-inputtext-sm"></p-inputNumber>
      </div>
    </div>

    <!-- Stitch Threshold -->
    <div class="field grid">
      <label for="stitchThreshold" class="col-12 mb-2 md:col-7 md:mb-0 text-black-alpha-90">
        Stitch Threshold
        <i class="pi pi-info-circle px-1"
           pTooltip="IoU threshold for stitching cell masks across Z-planes. Set > 0 to 
					 enable inter-slice stitching (alternative to true 3D mode). Default: 0 (disabled)."
           tooltipPosition="top"></i>
      </label>
      <div class="col-12 md:col-5">
        <p-inputNumber id="stitchThreshold" formControlName="stitchThreshold"
                       [showButtons]="true" [min]="0" [max]="1" [step]="0.1"
                       [minFractionDigits]="1" [maxFractionDigits]="2"
                       mode="decimal"
                       class="p-inputtext-sm"></p-inputNumber>
      </div>
    </div>

  </p-panel>

  </ng-container>
  <!-- end v3-only block -->

  <!-- ═══════════════════════ REGION & SLIDE CROPPING ════════════════════════ -->
  <!-- Shown only when the run will be auto-prepended with a crop step: a
       whole-slide format is selected (Cellpose can't open it directly) or the
       user has drawn regions. The slide is cropped to region-sized tiles first,
       then Cellpose segments each tile (one run per region). -->
  <ng-container *ngIf="needsCropPrefix()">
    <p-divider align="left">
      <div class="inline-flex align-items-center font-bold">Region &amp; Slide Cropping</div>
    </p-divider>

    <div class="flex align-items-start gap-2 p-2 mb-2 border-round surface-100 text-sm">
      <i class="pi pi-info-circle mt-1 text-primary"></i>
      <span>
        This image will be cropped before segmentation. Cellpose runs on each
        drawn region (or, if none are drawn, on auto-detected tissue regions)
        rather than on the whole slide.
      </span>
    </div>

    <div class="field grid">
      <label for="cropResolution" class="col-12 mb-2 md:col-7 md:mb-0 text-black-alpha-90">
        Crop Resolution
        <i class="pi pi-info-circle px-1"
           pTooltip="Pyramid level the crop is taken at, listed as level: widthxheight.
           Level 0 is full resolution; higher levels are downsampled (smaller crops,
           faster, less detail). Use full resolution for cell-level segmentation."
           tooltipPosition="top"></i>
      </label>
      <div class="col-12 md:col-5">
        <p-dropdown
          id="cropResolution"
          formControlName="cropResolution"
          placeholder="Select Resolution"
          [options]="levels"
          optionLabel="shape"
          optionValue="level"
        ></p-dropdown>
      </div>
    </div>
  </ng-container>

  <!-- ═══════════════════════════ FILE DESTINATION ══════════════════════════ -->
  <p-divider align="left">
    <div class="inline-flex align-items-center font-bold">File Destination</div>
  </p-divider>

  <!-- Extra output: instance-label mask PNG (applies to both v3 and v4). -->
  <div class="field-checkbox">
    <p-checkbox formControlName="saveMaskPng" [binary]="true" class="my-1" id="saveMaskPng"></p-checkbox>
    <label for="saveMaskPng">
      Save mask as PNG
      <i class="pi pi-info-circle px-2"
         pTooltip="Also write the segmentation mask as {basename}_mask.png next to the
         GeoJSON/CSV. Pixel value = cell id (0 = background); ids match the GeoJSON."
         tooltipPosition="top"></i>
    </label>
  </div>

  <div class="field grid h-3rem align-items-center">
    <label for="outputFolder" class="col-12 md:col-5 mb-0 text-black-alpha-90">
      Output folder:
      <i class="pi pi-info-circle px-1"
         pTooltip="Output folder name, created inside the same directory as the input image(s). 
				 Leave blank to write GeoJSON results directly alongside the input image(s)."
         tooltipPosition="top"></i>
    </label>
    <div class="col-12 md:col-7">
      <ng-container *ngIf="form.get('editOutputFolder')?.value; else viewOutputFolder">
        <div class="output-folder-edit">
          <input id="outputFolder" type="text" class="p-inputtext-sm w-full"
                 formControlName="outputFolder" pInputText/>
          <i class="pi pi-times icon-hover"
             (click)="form.get('editOutputFolder')?.setValue(false)"></i>
        </div>
      </ng-container>
      <ng-template #viewOutputFolder>
        {{ form.get('outputFolder')?.value }}
        <i class="pi pi-pencil icon-hover"
           (click)="form.get('editOutputFolder')?.setValue(true)"></i>
      </ng-template>
    </div>
  </div>

  <div class="pt-3 pb-3 process-buttons flex align-items-center gap-3">
    <app-submit-button
      label="Run"
      [isSubmitting]="isSubmitting"
      [processName]="processName"
      [validationMessage]="validationMessage"
      icon="pi pi-play"
      styleClass="p-button-primary"
      [tooltip]="'Run ' + processName"
    ></app-submit-button>
    <button pButton type="button" label="Create Rule" icon="pi pi-bell" class="p-button-outlined"
            [disabled]="(canCreateRule$ | async) === false || needsCropPrefix() || form.invalid"
            [pTooltip]="needsCropPrefix()
              ? 'Rules do not support the crop step this slide/regions need.'
              : 'Auto-run this on every new file in the selected folder.'"
            (click)="onCreateRule()"></button>
  </div>

</form>
